import apiService from '../service/ApiService';

const api = apiService.getApiClient();

// ── Types ────────────────────────────────────────────────────────────────────

export interface Domain {
  id: string;
  name: string;
  description: string;
  primaryKey?: string | null;
  editable?: boolean;
}

export interface Field {
  id: string;
  label: string;
  type: string;
  autoGenerated?: boolean;
}

export interface ColumnMeta {
  key: string;
  label: string;
}

export type CellValue = string | number | boolean | null;

export interface SearchResult {
  columns: ColumnMeta[];
  data: Record<string, CellValue>[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface HierarchyNode {
  id: string;
  label: string;
  children?: HierarchyNode[];
  isLeaf?: boolean;
}

export interface MetadataSchema {
  root: HierarchyNode[];
  maxDepth: number;
}

let domainsPromise: Promise<Domain[]> | null = null;
const fieldsPromiseByDomain = new Map<string, Promise<Field[]>>();
let metadataSchemaPromise: Promise<MetadataSchema> | null = null;
const childrenPromiseByKey = new Map<string, Promise<HierarchyNode[]>>();
const metadataFieldsPromiseByPath = new Map<string, Promise<Field[]>>();

const getOrCreateCachedPromise = <T>(
  cache: Map<string, Promise<T>>,
  key: string,
  loader: () => Promise<T>,
): Promise<T> => {
  const cached = cache.get(key);
  if (cached) {
    return cached;
  }

  const request = loader().catch((error) => {
    cache.delete(key);
    throw error;
  });

  cache.set(key, request);
  return request;
};

// ── Existing endpoints ───────────────────────────────────────────────────────

/** GET /api/domains */
export const getDomains = async (): Promise<Domain[]> => {
  if (!domainsPromise) {
    domainsPromise = api.get<Domain[]>('/api/domains')
      .then(({ data }) => data)
      .catch((error) => {
        domainsPromise = null;
        throw error;
      });
  }

  return domainsPromise;
};

/** GET /api/domains/{domainId}/fields */
export const getFields = async (domainId: string): Promise<Field[]> => {
  return getOrCreateCachedPromise(fieldsPromiseByDomain, domainId, async () => {
    const { data } = await api.get<Field[]>(`/api/domains/${domainId}/fields`);
    return data;
  });
};

/** POST /api/domains/{domainId}/search */
export const searchData = async (
  domainId: string,
  fields: string[],
  filters: Record<string, string>,
  page = 1,
  pageSize = 20,
) => {
  const { data } = await api.post(`/api/domains/${domainId}/search`, {
    fields, filters, page, pageSize,
  });
  return data;
};

// ── New metadata endpoints ───────────────────────────────────────────────────

/**
 * GET /api/metadata/schema
 * Returns root domain nodes and maxDepth (always 2: domain → table → fields).
 */
export const getMetadataSchema = async (): Promise<MetadataSchema> => {
  if (!metadataSchemaPromise) {
    metadataSchemaPromise = api.get<MetadataSchema>('/api/metadata/schema')
      .then(({ data }) => data)
      .catch((error) => {
        metadataSchemaPromise = null;
        throw error;
      });
  }

  return metadataSchemaPromise;
};

/**
 * GET /api/metadata/children?path=domain&level=1
 * Returns table nodes for a given domain. isLeaf=true means clicking shows fields.
 */
export const getChildrenAtLevel = async (
  path: string[],
  level: number,
): Promise<HierarchyNode[]> => {
  const cacheKey = `${path.join(',')}::${level}`;
  return getOrCreateCachedPromise(childrenPromiseByKey, cacheKey, async () => {
    const { data } = await api.get<HierarchyNode[]>('/api/metadata/children', {
      params: { path: path.join(','), level },
    });
    return data;
  });
};

/**
 * GET /api/metadata/fields?path=domain,table
 * Wrapper around GET /api/domains/{table}/fields — returns selectable columns.
 */
export const getFieldsForPath = async (path: string[]): Promise<Field[]> => {
  const pathKey = path.join(',');
  return getOrCreateCachedPromise(metadataFieldsPromiseByPath, pathKey, async () => {
    const { data } = await api.get<Field[]>('/api/metadata/fields', {
      params: { path: pathKey },
    });

    const leafEntityId = path[path.length - 1];
    if (leafEntityId && !fieldsPromiseByDomain.has(leafEntityId)) {
      fieldsPromiseByDomain.set(leafEntityId, Promise.resolve(data));
    }

    return data;
  });
};

// ── Inline edit ──────────────────────────────────────────────────────────────

export interface UpdateCellResult {
  primaryKey: string;
  id: string | number;
  column: string;
  value: CellValue;
}

/** PATCH /api/domains/{domainId}/rows/{rowId} */
export const updateCell = async (
  domainId: string,
  rowId: string | number,
  column: string,
  value: CellValue,
): Promise<UpdateCellResult> => {
  const { data } = await api.patch<UpdateCellResult>(
    `/api/domains/${domainId}/rows/${encodeURIComponent(String(rowId))}`,
    { column, value },
  );
  return data;
};
